/**
 * XP is granted only by the server's award_xp(reason, ref). The client names WHAT was
 * earned (a lesson step once, a card review once a day); the server fixes the amount,
 * checks the evidence, enforces the daily cap and ignores repeats of the same ref.
 */

export type AwardReason = 'exercise' | 'review';

export interface XpAward {
  reason: AwardReason;
  ref: string;
}

/** Same pattern as the server's check in award_xp(). */
const EXERCISE_REF = /^[A-Za-z0-9._-]+:[A-Za-z0-9._:|,-]+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** One award per lesson step, ever: ref `<lesson_id>:<step_key>`. */
export function exerciseAward(lessonId: string, stepKey: string): XpAward | null {
  const ref = `${lessonId}:${stepKey}`;
  return EXERCISE_REF.test(ref) ? { reason: 'exercise', ref } : null;
}

/** One award per card per day; the server checks the matching review_log row. */
export function reviewAward(cardId: string): XpAward | null {
  return UUID.test(cardId) ? { reason: 'review', ref: cardId } : null;
}

/**
 * Converts an XP op queued by an older client version (`reason` was
 * `exercise:<lesson>:<step>` or `review:<card id>`) into an award, or null to drop it.
 */
export function awardFromLegacyReason(reason: string): XpAward | null {
  const [kind, ...rest] = reason.split(':');
  if (kind === 'exercise' && rest.length >= 2) return exerciseAward(rest[0]!, rest.slice(1).join(':'));
  if (kind === 'review' && rest.length === 1) return reviewAward(rest[0]!);
  return null;
}
